import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { assetApi } from "../api";
import { useAsyncData } from "../hooks";
import { BackBtn, Btn, Card, CardHeader, PageHeader } from "../components/AssetUI";
import type { AssetCondition, AssetCreateRequest } from "../types";

const CONDITIONS: AssetCondition[] = ["EXCELLENT", "GOOD", "FAIR", "POOR", "DAMAGED"];

const INIT: AssetCreateRequest = {
  assetNumber:        "",
  name:               "",
  categoryId:         0,
  condition:          "GOOD",
  locationId:         undefined,
  description:        "",
  serialNumber:       "",
  manufacturer:       "",
  brand:              "",
  model:              "",
  purchaseDate:       "",
  purchaseCost:       undefined,
  currentValue:       undefined,
  supplier:           "",
  invoiceNumber:      "",
  warrantyExpiry:     "",
  depreciationMethod: "",
  notes:              "",
};

export default function AssetAddPage() {
  const navigate = useNavigate();
  const [form, setForm]     = useState<AssetCreateRequest>(INIT);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const cats = useAsyncData(() => assetApi.listCategories(), []);
  const locs = useAsyncData(() => assetApi.listLocations(),  []);

  function set<K extends keyof AssetCreateRequest>(k: K, v: AssetCreateRequest[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.assetNumber.trim()) { setError("Asset number is required."); return; }
    if (!form.name.trim())        { setError("Asset name is required."); return; }
    if (!form.categoryId)         { setError("Please select a category."); return; }
    setSaving(true);
    setError(null);
    try {
      // Strip empty optional strings so the backend receives null rather than ""
      const payload: AssetCreateRequest = {
        ...form,
        locationId:         form.locationId || undefined,
        serialNumber:       form.serialNumber       || undefined,
        manufacturer:       form.manufacturer       || undefined,
        brand:              form.brand              || undefined,
        model:              form.model              || undefined,
        purchaseDate:       form.purchaseDate       || undefined,
        supplier:           form.supplier           || undefined,
        invoiceNumber:      form.invoiceNumber      || undefined,
        warrantyExpiry:     form.warrantyExpiry     || undefined,
        depreciationMethod: form.depreciationMethod || undefined,
        description:        form.description        || undefined,
        notes:              form.notes              || undefined,
      };
      const created = await assetApi.createAsset(payload);
      navigate(`/assets/${created.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save asset.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Asset Management · Assets"
        title="Add New Asset"
        actions={<BackBtn to="/assets/list" label="Asset List" />}
      />

      <form onSubmit={submit} className="space-y-5">
        {/* Identification */}
        <Card noPad>
          <CardHeader title="Identification" subtitle="Required fields are marked with *" />
          <div className="grid gap-4 p-5 md:grid-cols-2">
            <Field label="Asset Number *">
              <input
                required
                className={inp}
                placeholder="e.g. EATTA-LAP-001"
                value={form.assetNumber}
                onChange={(e) => set("assetNumber", e.target.value)}
              />
            </Field>
            <Field label="Asset Name *">
              <input
                required
                className={inp}
                placeholder="e.g. Dell Latitude 5540"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Category *">
              <select
                required
                className={inp}
                value={form.categoryId || ""}
                onChange={(e) => set("categoryId", Number(e.target.value))}
              >
                <option value="">Select category…</option>
                {cats.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <select
                className={inp}
                value={form.locationId ?? ""}
                onChange={(e) => set("locationId", e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">No location assigned</option>
                {locs.data?.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
            <Field label="Condition *">
              <select
                required
                className={inp}
                value={form.condition}
                onChange={(e) => set("condition", e.target.value as AssetCondition)}
              >
                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Serial Number">
              <input
                className={inp}
                placeholder="e.g. DL5540CN001TZ24"
                value={form.serialNumber ?? ""}
                onChange={(e) => set("serialNumber", e.target.value)}
              />
            </Field>
          </div>
        </Card>

        {/* Hardware / make */}
        <Card noPad>
          <CardHeader title="Make & Model" />
          <div className="grid gap-4 p-5 md:grid-cols-3">
            <Field label="Manufacturer">
              <input className={inp} placeholder="e.g. Dell" value={form.manufacturer ?? ""} onChange={(e) => set("manufacturer", e.target.value)} />
            </Field>
            <Field label="Brand">
              <input className={inp} placeholder="e.g. Dell" value={form.brand ?? ""} onChange={(e) => set("brand", e.target.value)} />
            </Field>
            <Field label="Model">
              <input className={inp} placeholder="e.g. Latitude 5540" value={form.model ?? ""} onChange={(e) => set("model", e.target.value)} />
            </Field>
          </div>
        </Card>

        {/* Financial */}
        <Card noPad>
          <CardHeader title="Financial Details" />
          <div className="grid gap-4 p-5 md:grid-cols-2">
            <Field label="Purchase Date">
              <input type="date" className={inp} value={form.purchaseDate ?? ""} onChange={(e) => set("purchaseDate", e.target.value)} />
            </Field>
            <Field label="Warranty Expiry">
              <input type="date" className={inp} value={form.warrantyExpiry ?? ""} onChange={(e) => set("warrantyExpiry", e.target.value)} />
            </Field>
            <Field label="Purchase Cost (KES)">
              <input
                type="number" min={0} step="0.01" className={inp}
                placeholder="0.00"
                value={form.purchaseCost ?? ""}
                onChange={(e) => set("purchaseCost", e.target.value ? Number(e.target.value) : undefined)}
              />
            </Field>
            <Field label="Current Value (KES)">
              <input
                type="number" min={0} step="0.01" className={inp}
                placeholder="0.00"
                value={form.currentValue ?? ""}
                onChange={(e) => set("currentValue", e.target.value ? Number(e.target.value) : undefined)}
              />
            </Field>
            <Field label="Supplier">
              <input className={inp} placeholder="e.g. TechHub Kenya" value={form.supplier ?? ""} onChange={(e) => set("supplier", e.target.value)} />
            </Field>
            <Field label="Invoice Number">
              <input className={inp} value={form.invoiceNumber ?? ""} onChange={(e) => set("invoiceNumber", e.target.value)} />
            </Field>
            <Field label="Depreciation Method">
              <select className={inp} value={form.depreciationMethod ?? ""} onChange={(e) => set("depreciationMethod", e.target.value || undefined)}>
                <option value="">None / Not set</option>
                <option value="STRAIGHT_LINE">Straight Line</option>
                <option value="DECLINING_BALANCE">Declining Balance</option>
              </select>
            </Field>
          </div>
        </Card>

        {/* Notes */}
        <Card noPad>
          <CardHeader title="Description & Notes" />
          <div className="grid gap-4 p-5">
            <Field label="Description">
              <textarea rows={2} className={`${inp} w-full resize-none`} placeholder="Brief description of the asset…"
                value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} />
            </Field>
            <Field label="Internal Notes">
              <textarea rows={2} className={`${inp} w-full resize-none`} placeholder="Any additional notes for internal use…"
                value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} />
            </Field>
          </div>
        </Card>

        {error && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
        )}

        <div className="flex justify-end gap-3 pb-6">
          <Btn variant="secondary" type="button" onClick={() => navigate("/assets/list")}>Cancel</Btn>
          <Btn variant="primary" type="submit" loading={saving} loadingText="Saving…">Save Asset</Btn>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 bg-white";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-slate-500">{label}</span>
      {children}
    </label>
  );
}
